import { functions } from '@app/firebase';
import UserContext from '@context/UserContext';
import { formatDate } from '@fullcalendar/core';
import Sentry from '@integrations/Sentry';
import { EmailOutlined } from '@mui/icons-material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import WeekendIcon from '@mui/icons-material/Weekend';
import { 
  Box, 
  Button, 
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography 
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import React, { useContext, useMemo, useState } from 'react';

import { GREEN_DARK } from '../../../ui/theme/theme';
import makeStoreUrl from '../../../ui/utils/makeStoreUrl';
import useUserInfo from '../../hooks/useUserInfo';
import { contactDataByMarket } from '../../pages/resources/contactData';

const sendQuoteEmail = httpsCallable<{ 
  quoteLink: string; 
  email: string; 
  sellerName: string; 
  agentName: string; 
  lineItems: HubSpot.LineItem[];
  optionalServices: string;
  address: string;
  salesLeadName: string;
  salesLeadPhone: string;
}, { success: boolean; message: string }>(functions, 'http-sendQuoteEmail');

interface QuoteCardProps {
  id: string;
  address: string;
  created: string;
  installDate?: string;
  lineItems?: HubSpot.LineItem[];
}

const QuoteCard = (props: QuoteCardProps) => {
  const { id, address, created, installDate, lineItems = [] } = props;
  const { firstname } = useUserInfo();
  const userContext = useContext<UserContext>(UserContext);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [sellerName, setSellerName] = useState('');
  const [emailError, setEmailError] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  
  const agentName = useMemo(() => {
    if (firstname) {
      return `${firstname}`;
    }
    return '';
  }, [firstname]);

  // Determine user's market based on activeRegion
  const userMarket = useMemo(() => {
    const regionName = userContext.activeRegion?.docID || '';
    
    if (regionName.includes('denver_boulder') || regionName.includes('denver') || regionName.includes('boulder')) {
      return 'colorado';
    }
    if (regionName.includes('california') || regionName.includes('san diego') || regionName.includes('orange county')) {
      return 'california';
    }
    
    // Default to California if region is unclear
    return 'california';
  }, [userContext.activeRegion]);

  // Get sales lead (first contact - "For new projects") for the user's market
  const salesLead = useMemo(() => {
    const contacts = contactDataByMarket[userMarket] || contactDataByMarket.california;

    return contacts[0]; // First contact is the sales lead for new projects
  }, [userMarket]);

  // Build optional_services string based on missing SKUs
  const optionalServices = useMemo(() => {
    const skus = new Set<string>();

    // Collect unique SKU prefixes from lineItems
    lineItems.forEach((lineItem) => {
      const sku = lineItem.properties?.hs_sku;

      if (sku) {
        const prefix = sku.split('_')[0];

        skus.add(prefix);
      }
    });

    // Define all available services with their display names
    const allServices = [
      {
        name: 'staging',
        checkSku: (skuSet: Set<string>) => Array.from(skuSet).some(sku => sku === 'STA' || sku.startsWith('STA') || sku === 'FRESH' || sku.startsWith('FRESH')),
      },
      {
        name: 'cleaning',
        checkSku: (skuSet: Set<string>) => Array.from(skuSet).some(sku => sku === 'CLE' || sku.startsWith('CLE')),
      },
      {
        name: 'photos',
        checkSku: (skuSet: Set<string>) => Array.from(skuSet).some(sku => 
          sku === 'PHO' || sku.startsWith('PHO') ||
          sku === 'FLO' || sku.startsWith('FLO') ||
          sku === 'VID' || sku.startsWith('VID') ||
          sku === 'DRO' || sku.startsWith('DRO') ||
          sku === 'MAT' || sku.startsWith('MAT') ||
          sku === 'TWI' || sku.startsWith('TWI') ||
          sku === 'TPE' || sku.startsWith('TPE')
        ),
      },
      {
        name: 'virtual staging',
        checkSku: (skuSet: Set<string>) => Array.from(skuSet).some(sku => sku === 'VIR' || sku.startsWith('VIR')),
      },
      {
        name: 'moving',
        checkSku: (skuSet: Set<string>) => Array.from(skuSet).some(sku => sku === 'MVR' || sku.startsWith('MVR')),
      },
    ];

    // Find missing services
    const missingServices = allServices
      .filter(service => !service.checkSku(skus))
      .map(service => service.name);

    return missingServices.join(', ');
  }, [lineItems]);

  const serviceIcons = useMemo(() => {
    const skus = new Set<string>();
    const inactiveColor = '#CCCCCC';

    // Collect unique SKU prefixes
    lineItems.forEach((lineItem) => {
      const sku = lineItem.properties?.hs_sku;

      if (sku) {
        // Extract prefix (first 3-4 characters before underscore or end of string)
        const prefix = sku.split('_')[0];

        skus.add(prefix);
      }
    });

    // Define all available services
    const allServices = [
      {
        icon: WeekendIcon,
        name: 'staging',
        checkSku: (skuSet: Set<string>) => Array.from(skuSet).some(sku => sku === 'STA' || sku.startsWith('STA') || sku === 'FRESH' || sku.startsWith('FRESH')),
      },
      {
        icon: CleaningServicesIcon,
        name: 'cleaning',
        checkSku: (skuSet: Set<string>) => Array.from(skuSet).some(sku => sku === 'CLE' || sku.startsWith('CLE')),
      },
      {
        icon: CameraAltIcon,
        name: 'media',
        checkSku: (skuSet: Set<string>) => Array.from(skuSet).some(sku => 
          sku === 'PHO' || sku.startsWith('PHO') ||
          sku === 'FLO' || sku.startsWith('FLO') ||
          sku === 'VID' || sku.startsWith('VID') ||
          sku === 'DRO' || sku.startsWith('DRO') ||
          sku === 'MAT' || sku.startsWith('MAT') ||
          sku === 'TWI' || sku.startsWith('TWI') ||
          sku === 'TPE' || sku.startsWith('TPE')
        ),
      },
      {
        icon: ViewInArIcon,
        name: 'virtual-staging',
        checkSku: (skuSet: Set<string>) => Array.from(skuSet).some(sku => sku === 'VIR' || sku.startsWith('VIR')),
      },
      {
        icon: LocalShippingIcon,
        name: 'moving',
        checkSku: (skuSet: Set<string>) => Array.from(skuSet).some(sku => sku === 'MVR' || sku.startsWith('MVR')),
      },
    ];

    // Return all services with their active/inactive state
    const services = allServices.map(service => ({
      icon: service.icon,
      isActive: service.checkSku(skus),
      color: service.checkSku(skus) ? GREEN_DARK : inactiveColor,
    }));

    // Calculate percentage of active services
    const activeCount = services.filter(s => s.isActive).length;
    const totalCount = services.length;
    const percentage = Math.round((activeCount / totalCount) * 100);

    return {
      services,
      activeCount,
      totalCount,
      percentage,
    };
  }, [lineItems]);

  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailValue) {
      setEmailError('Email address is required');
      return false;
    }
    if (!emailRegex.test(emailValue)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSendQuote = async () => {
    if (!validateEmail(email)) {
      return;
    }

    if (!sellerName.trim()) {
      setSendError('Seller name is required');
      return;
    }

    setSending(true);
    setSendError('');

    try {
      await sendQuoteEmail({ 
        quoteLink: makeStoreUrl(`/home-staging/get-a-quote/${id}`), 
        email,
        sellerName: sellerName.trim(),
        agentName,
        lineItems,
        optionalServices,
        address,
        salesLeadName: salesLead.name,
        salesLeadPhone: salesLead.phone,
      });
      setOpen(false);
      setEmail('');
      setSellerName('');
      setEmailError('');
    } catch (e) {
      Sentry.captureException(e);
      setSendError(e.message || 'Failed to send quote email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEmail('');
    setSellerName('');
    setEmailError('');
    setSendError('');
  };

  return (
    <>
      <Paper
        role="button"
        tabIndex={0}
        sx={{
          padding: 2,
          borderRadius: 1.25,
          backgroundColor: '#ffffff',
          border: '1px solid #EBEBEB',
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          style={{ gap: 12 }}
        >
          <Typography
            variant="h5Alt"
            style={{ marginBottom: 6 }}
          >
            {address}
          </Typography>
          <Box
            display="flex"
            gap={0.5}
            alignItems="center"
          >
            <Tooltip title="Review & Pay">
              <IconButton
                size="small"
                sx={{ color: '#080808' }}
                onClick={() => {
                  window.open(makeStoreUrl(`/home-staging/get-a-quote/${id}`), '_blank')?.focus();
                }}
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share">
              <IconButton
                size="small"
                sx={{ color: '#080808' }}
                onClick={() => setOpen(true)}
              >
                <EmailOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Typography
          variant="body2"
          style={{ opacity: 0.85 }}
        >
          {created ? `Created ${formatDate(created)}` : 'Created date unavailable'}
        </Typography>
        <Typography
          variant="body2"
          style={{ opacity: 0.85, marginBottom: 24 }}
        >
          {installDate ? `Target install ${formatDate(installDate)}` : 'Target install date not set'}
        </Typography>
        <Box
          display="flex"
          flexDirection="column"
          gap={0.5}
          style={{ marginTop: 'auto' }}
        >
          <Box
            display="flex"
            gap={1}
            alignItems="center"
          >
            {serviceIcons.services.map((service, index) => {
              const IconComponent = service.icon;

              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: service.isActive ? `${service.color}15` : '#F5F5F5',
                    color: service.color,
                    opacity: service.isActive ? 1 : 0.5,
                  }}
                >
                  <IconComponent sx={{ fontSize: 18 }} />
                </Box>
              );
            })}
          </Box>
          {serviceIcons.percentage === 100 ? (
            <Typography
              variant="caption"
              style={{
                fontSize: 13,
                color: '#4CAF50',
                fontWeight: 500,
              }}
            >
              100% of recommended services. Great work!
            </Typography>
          ) : (
            <Typography
              variant="caption"
              style={{
                fontSize: 13,
                color: '#666666',
                fontWeight: 500,
              }}
            >
              {serviceIcons.percentage}
              % of recommended services. Add more to sell faster.
            </Typography>
          )}
        </Box>
      </Paper>

      <Dialog
        fullWidth
        open={open}
        maxWidth="md"
        PaperProps={{
          sx: {
            maxHeight: '95vh',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
        onClose={handleClose}
      >
        <DialogTitle>
          Send Quote
        </DialogTitle>

        <DialogContent sx={{ pb: 0 }}>
          {/* Subject */}
          <TextField
            fullWidth
            label="Subject"
            margin="dense"
            value={`Your Personalized Listing Prep Plan from ${agentName || 'Guest House'}`}
            variant="outlined"
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
          />

          {/* Send to */}
          <TextField
            autoFocus
            fullWidth
            error={!!emailError}
            helperText={emailError}
            label="Send to"
            margin="dense"
            placeholder="recipient@example.com"
            type="email"
            value={email}
            variant="outlined"
            sx={{ mb: 2 }}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) {
                validateEmail(e.target.value);
              }
            }}
            onBlur={() => validateEmail(email)}
          />

          {/* Seller Name */}
          <TextField
            fullWidth
            label="Seller Name"
            margin="dense"
            placeholder="Enter seller name"
            value={sellerName}
            variant="outlined"
            onChange={(e) => {
              setSellerName(e.target.value);
              if (sendError && !sellerName.trim()) {
                setSendError('');
              }
            }}
          />

          {sendError && (
            <Typography
              variant="body2"
              sx={{ mt: 2, color: 'error.main' }}
            >
              {sendError}
            </Typography>
          )}

          {/* Email Preview Label */}
          <Typography
            variant="subtitle2"
            sx={{ mt: 3, mb: 1.5, color: 'text.secondary' }}
          >
            Email Preview
          </Typography>
        </DialogContent>

        {/* Email Preview - Scrollable */}
        <Box
          sx={{ 
            backgroundColor: '#F7F7F7',
            border: '1px solid #EBEBEB',
            borderRadius: 1,
            mx: 3,
            mb: 2,
            flex: 1,
            overflow: 'auto',
            minHeight: 200,
            maxHeight: 600,
          }}
        >
          <Box sx={{ maxWidth: 600, mx: 'auto', p: 2, backgroundColor: '#fff' }}>
            {/* Logo */}
            <Box sx={{ py: 1.5, px: 2.5 }}>
              <Box
                component="img"
                src="https://8563089.fs1.hubspotusercontent-na2.net/hubfs/8563089/Logo/GH_Logo.png"
                alt="Guest House"
                sx={{ width: 200, height: 'auto' }}
              />
            </Box>

            {/* Main Content */}
            <Box sx={{ py: 3 }}>
              {/* Greeting */}
              <Box sx={{ px: 2.5, mb: 2 }}>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: '#000',
                    mb: 1.5,
                  }}
                >
                  Hi
                  {' '}
                  {sellerName || 'there'}
                  ,
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: '#000',
                  }}
                >
                  Great news! I&apos;ve put together a customized staging and services plan designed to showcase your home&apos;s best features and maximize appeal to today&apos;s buyers.
                </Typography>
              </Box>

              {/* Why Staging Matters */}
              <Box sx={{ px: 2.5, mt: 3 }}>
                <Typography
                  sx={{
                    fontFamily: 'sans-serif',
                    fontWeight: 600,
                    fontSize: 24,
                    lineHeight: 1.25,
                    color: '#000',
                    mb: 1.5,
                  }}
                >
                  Why staging matters for your home:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: '#000',
                  }}
                >
                  Professionally staged homes sell 21% faster and for 5–15% more than non-staged homes. With buyers forming opinions in seconds online, first impressions are everything.
                </Typography>
              </Box>

              {/* Your Personalized Quote Includes */}
              <Box sx={{ px: 2.5, mt: 4 }}>
                <Typography
                  sx={{
                    fontFamily: 'sans-serif',
                    fontWeight: 600,
                    fontSize: 24,
                    lineHeight: 1.25,
                    color: '#000',
                    mb: 2,
                  }}
                >
                  Your personalized quote includes:
                </Typography>

                {/* Core Line Items */}
                {lineItems
                  .filter(item => {
                    const sku = item.properties?.hs_sku || '';

                    return sku.startsWith('STA') || sku.startsWith('FRESH');
                  })
                  .map((item, index) => (
                    <Box
                      key={item.id || `core-${index}`}
                      sx={{ mb: 1.5 }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 15,
                          lineHeight: 1.75,
                          color: '#080808',
                        }}
                      >
                        {item.properties?.name || 'Service'}
                        {item.properties?.quantity && parseInt(item.properties.quantity) > 1 
                          ? ` x${item.properties.quantity}` 
                          : ''}
                      </Typography>
                      <Divider sx={{ borderStyle: 'dashed', borderColor: '#000', mt: 1 }} />
                    </Box>
                  ))}
              </Box>

              {/* Add-ons */}
              {lineItems.filter(item => {
                const sku = item.properties?.hs_sku || '';

                return !sku.startsWith('STA') && !sku.startsWith('FRESH');
              }).length > 0 && (
                <Box sx={{ px: 2.5, mt: 4 }}>
                  <Typography
                    sx={{
                      fontFamily: 'sans-serif',
                      fontWeight: 600,
                      fontSize: 24,
                      lineHeight: 1.25,
                      color: '#000',
                      mb: 2,
                    }}
                  >
                    Add-ons:
                  </Typography>

                  {lineItems
                    .filter(item => {
                      const sku = item.properties?.hs_sku || '';

                      return !sku.startsWith('STA') && !sku.startsWith('FRESH');
                    })
                    .map((item, index) => (
                      <Box
                        key={item.id || `addon-${index}`}
                        sx={{ mb: 1.5 }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: 15,
                            lineHeight: 1.75,
                            color: '#080808',
                          }}
                        >
                          {item.properties?.name || 'Service'}
                          {item.properties?.quantity && parseInt(item.properties.quantity) > 1 
                            ? ` x${item.properties.quantity}` 
                            : ''}
                        </Typography>
                        <Divider sx={{ borderStyle: 'dashed', borderColor: '#000', mt: 1 }} />
                      </Box>
                    ))}
                </Box>
              )}

              {/* Optional Services */}
              {optionalServices && (
                <Box sx={{ px: 2.5, mt: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: 14,
                      lineHeight: 1.75,
                      color: '#000',
                    }}
                  >
                    Optional services like
                    {' '}
                    {optionalServices}
                    {' '}
                    are also available.
                    {' '}
                    <Box
                      component="span"
                      sx={{ fontWeight: 600, textDecoration: 'underline' }}
                    >
                      Prep Now, Pay Later
                    </Box>
                    {' '}
                    option so all costs can be paid at closing.
                  </Typography>
                </Box>
              )}

              {/* Prep Now, Pay Later Section */}
              <Box sx={{ px: 2.5, mt: 6 }}>
                <Typography
                  sx={{
                    fontFamily: 'sans-serif',
                    fontWeight: 600,
                    fontSize: 24,
                    lineHeight: 1.25,
                    color: '#000',
                    mb: 1.5,
                  }}
                >
                  Prep Now, Pay Later
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: '#000',
                  }}
                >
                  Our Prep Now, Pay Later option allows homeowners to pay everything at closing. Learn more
                  {' '}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, textDecoration: 'underline' }}
                  >
                    here
                  </Box>
                  .
                </Typography>
              </Box>

              {/* CTA Link */}
              <Box sx={{ px: 2.5, mt: 4, mb: 6 }}>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: '#000',
                  }}
                >
                  👉
                  {' '}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, textDecoration: 'underline' }}
                  >
                    Click here to review your quote and secure your service date
                  </Box>
                </Typography>
              </Box>

              {/* Why I Partner */}
              <Box sx={{ px: 2.5, mt: 3 }}>
                <Typography
                  sx={{
                    fontFamily: 'sans-serif',
                    fontWeight: 600,
                    fontSize: 24,
                    lineHeight: 1.25,
                    color: '#000',
                    mb: 1.5,
                  }}
                >
                  Why I partner with Guest House:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: '#000',
                    mb: 1,
                  }}
                >
                  They make listing prep simple, so we can focus on selling quickly and for top dollar.
                </Typography>
                <Typography
                  component="div"
                  sx={{ fontFamily: 'monospace', fontSize: 14, lineHeight: 1.75, color: '#000' }}
                >
                  • 2,500+ listings, $2.5B sold
                  <br />
                  • Fast scheduling and top quality guaranteed
                  <br />
                  • Vetted professionals &amp; transparent pricing
                  <br />
                  • One easy platform to review, approve and track work
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: '#000',
                    mt: 2,
                  }}
                >
                  You can check out some of their work
                  {' '}
                  <Box
                    component="span"
                    sx={{ textDecoration: 'underline' }}
                  >
                    here
                  </Box>
                  .
                </Typography>
              </Box>

              {/* What Happens Next */}
              <Box sx={{ px: 2.5, mt: 3 }}>
                <Typography
                  sx={{
                    fontFamily: 'sans-serif',
                    fontWeight: 600,
                    fontSize: 24,
                    lineHeight: 1.25,
                    color: '#000',
                    mb: 1.5,
                  }}
                >
                  What happens next:
                </Typography>
                <Typography
                  component="div"
                  sx={{ fontFamily: 'monospace', fontSize: 14, lineHeight: 1.75, color: '#000' }}
                >
                  • Review your quote
                  <br />
                  •
                  {' '}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, textDecoration: 'underline' }}
                  >
                    Approve and pay
                  </Box>
                  {' '}
                  to lock in your date
                  <br />
                  • The team handles the rest — your home becomes a buyer magnet
                </Typography>
              </Box>

              {/* Timing / Closing */}
              <Box sx={{ px: 2.5, mt: 3 }}>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: '#000',
                    mb: 2,
                  }}
                >
                  ⏰
                  {' '}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600 }}
                  >
                    Timing matters:
                  </Box>
                  {' '}
                  Service calendars fill quickly. Securing your spot now ensures we&apos;re ready to list and capture maximum buyer attention from day one.
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: '#000',
                    mb: 2,
                  }}
                >
                  Here&apos;s my partner at Guest House, feel free to call her if you have any questions:
                  {' '}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600 }}
                  >
                    {salesLead.name}
                  </Box>
                  {' '}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, textDecoration: 'underline' }}
                  >
                    {salesLead.phone}
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: '#000',
                    mb: 1.5,
                  }}
                >
                  Let&apos;s get your home prepped and sold for top dollar!
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: '#000',
                  }}
                >
                  {agentName || 'Your Agent'}
                </Typography>
              </Box>

              {/* Footer */}
              <Box sx={{ px: 2.5, mt: 4, pt: 4 }}>
                <Typography
                  sx={{
                    fontFamily: 'Arial, sans-serif',
                    fontSize: 12,
                    lineHeight: 1.35,
                    color: '#000',
                  }}
                >
                  This email is not monitored. To get in touch please
                  {' '}
                  <Box
                    component="span"
                    sx={{ textDecoration: 'underline' }}
                  >
                    contact our team
                  </Box>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <DialogActions>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={!email || !sellerName.trim() || !!emailError || sending}
            endIcon={<EmailOutlined />}
            onClick={handleSendQuote}
          >
            {sending ? 'Sending...' : 'Send Quote'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuoteCard;
